<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useVoucherSSBTStore } from '@/stores/voucher/voucherSSBTStore'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  useVoucher,
  useVoucherStore,
  voucherErrorParser,
  vouchersApiService,
  VoucherStatus
} from '@nsftx/seven-client-retail-vue-sgp'
import {
  NXGDSButton,
  NXGDSButtonVariants,
  NXGDSCondensedIndicator,
  NXGDSCondensedIndicatorVariants,
  NXGDSModal,
  NXGDSStatusTag,
  NXGDSStatusTagVariants
} from '@nsftx/seven-nxg-design-system'
import i18n from '@/plugins/translationsPlugin'
import { printVoucher } from '@/modules/voucher/voucherSSBTService'
import { logService } from '@nsftx/seven-client-retail-utils'
import { formatNumber, useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { getWallets } from '@/modules/wallet/walletService'
import errorParser from '@/utils/errorParser'

const LOG_PREFIX = '[VoucherDetails]'
const MS_IN_MINUTE = 60_000

const { t } = i18n.global
const voucherSSBTStore = useVoucherSSBTStore()
const { isVoucherDetailsOpened, isVoucherPinEnabled } = storeToRefs(voucherSSBTStore)
const { closeVoucherDetailsModal, showVoucherPinModal } = voucherSSBTStore
const { appData } = storeToRefs(useDeviceManagementStore())
const { showNotification } = useNotificationsStore()

const voucherStore = useVoucherStore()
const { voucher, voucherPin } = storeToRefs(voucherStore)
const { isVoucherRedeemable } = useVoucher()

const formatDateTimeToLocaleString = (date: string): string => {
  const parsedDate = new Date(date)

  if (Number.isNaN(parsedDate.getTime())) {
    return '-'
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'medium'
  }).format(parsedDate)
}

const formatRemainingValidity = (validUntil: string): string => {
  const expiresAt = new Date(validUntil).getTime()

  if (Number.isNaN(expiresAt)) {
    return '-'
  }

  const diffMs = Math.max(0, expiresAt - Date.now())
  const totalMinutes = Math.floor(diffMs / MS_IN_MINUTE)
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60

  return `${days}d ${hours}h ${minutes}min`
}

const headerTagData = computed(() => {
  if (voucher.value?.status.resolutionStatus === VoucherStatus.EXPIRED) {
    return {
      label: t('voucher_details_header_status_expired'),
      variant: NXGDSStatusTagVariants.negative
    }
  }

  if (voucher.value?.status.resolutionStatus === VoucherStatus.REDEEMED) {
    return {
      label: t('voucher_details_header_status_redeemed'),
      variant: NXGDSStatusTagVariants.positive
    }
  }

  if (voucher.value?.status.resolutionStatus === VoucherStatus.REVOKED) {
    return {
      label: t('voucher_details_header_status_revoked'),
      variant: NXGDSStatusTagVariants.subtle
    }
  }

  return {
    label: t('voucher_details_header_status_valid', {
      date: formatRemainingValidity(voucher.value?.context.validUntil ?? '')
    }),
    variant: NXGDSStatusTagVariants.attention
  }
})

const voucherTableRow = computed(() => {
  if (!voucher.value) {
    return null
  }

  return {
    amount: formatNumber(voucher.value.context.amount, {
      style: 'currency',
      currencySymbol: voucher.value.context.currency
    }),
    validUntil: formatDateTimeToLocaleString(voucher.value.context.validUntil),
    issuedIn: voucher.value.context.issuedIn,
    redeemedAt: voucher.value.activity?.usedAt
      ? formatDateTimeToLocaleString(voucher.value.activity.usedAt)
      : '-',
    redeemedIn: voucher.value.activity?.usedIn
      ? `${voucher.value.activity.usedIn.locationName}`
      : '-'
  }
})

const redeemVoucher = async () => {
  if (voucherPin.value === null && isVoucherPinEnabled.value) {
    closeVoucherDetailsModal()
    showVoucherPinModal()
    return
  }

  try {
    await vouchersApiService.redeemVoucher(
      appData.value!.betshop.companyUuid,
      voucher.value!.id,
      voucherPin.value ?? ''
    )

    logService.info(`${LOG_PREFIX} Voucher redeemed successfully`, {
      code: 'ST_VOUCHER_REDEEM_SUCCESS'
    })

    getWallets('ui').catch((err) => {
      logService.error(`${LOG_PREFIX} Failed to get wallets for the punter after voucher redeem.`, {
        code: 'ST_GET_WALLETS_FAILED',
        ...errorParser.parseUpstream(err)
      })
    })

    await printVoucher(voucher.value!, 'payout')

    closeVoucherDetailsModal()
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to redeem voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_REDEEM_ERROR'
    })
    showNotification({
      variant: 'error',
      text: [t('voucher_redeem_failed')]
    })
  }
}
</script>

<template>
  <NXGDSModal
    v-model="isVoucherDetailsOpened"
    :show-close-button="true"
    :title="t('common_voucher')"
    width="1200px"
    class="st-voucher-details-modal"
  >
    <template #header>
      <div class="st-voucher-details-modal__header">
        <div class="st-voucher-details-modal__header-title">
          <h4>{{ t('common_voucher') }}</h4>
          <NXGDSStatusTag :label="headerTagData.label" :variant="headerTagData.variant" />
        </div>
        <div class="st-voucher-details-modal__header-subtitle">
          {{ formatDateTimeToLocaleString(voucher?.context.issuedAt ?? '') }}
          <NXGDSCondensedIndicator :variant="NXGDSCondensedIndicatorVariants.universal" />
          {{ t('deposits_vouchers_table_header_voucher_id') }}: {{ voucher?.barcode }}
        </div>
      </div>
    </template>

    <table v-if="voucher && voucherTableRow" class="st-voucher-details-modal__table">
      <thead class="st-voucher-details-modal__table-thead">
        <tr class="st-voucher-details-modal__table-thead-row">
          <td class="st-voucher-details-modal__table-cell">
            {{ t('voucher_details_header_amount') }}
          </td>
          <td class="st-voucher-details-modal__table-cell">
            {{ t('voucher_details_header_valid_until') }}
          </td>
          <td class="st-voucher-details-modal__table-cell">
            {{ t('voucher_details_header_issued_in') }}
          </td>
          <td class="st-voucher-details-modal__table-cell">
            {{ t('voucher_details_header_redeemed_at') }}
          </td>
          <td class="st-voucher-details-modal__table-cell">
            {{ t('voucher_details_header_redeemed_in') }}
          </td>
        </tr>
      </thead>
      <tbody class="st-voucher-details-modal__table-tbody">
        <tr class="st-voucher-details-modal__table-tbody-row">
          <td class="st-voucher-details-modal__table-cell">{{ voucherTableRow.amount }}</td>
          <td class="st-voucher-details-modal__table-cell">{{ voucherTableRow.validUntil }}</td>
          <td class="st-voucher-details-modal__table-cell">{{ voucherTableRow.issuedIn }}</td>
          <td class="st-voucher-details-modal__table-cell">{{ voucherTableRow.redeemedAt }}</td>
          <td class="st-voucher-details-modal__table-cell">{{ voucherTableRow.redeemedIn }}</td>
        </tr>
      </tbody>
    </table>

    <template #footer>
      <div class="st-voucher-details-modal__actions">
        <NXGDSButton :variant="NXGDSButtonVariants.secondary" @click="closeVoucherDetailsModal">
          {{ t('common_close') }}
        </NXGDSButton>
        <NXGDSButton
          v-if="isVoucherRedeemable()"
          :variant="NXGDSButtonVariants.primary"
          @click="redeemVoucher"
        >
          {{ t('voucher_activate') }}
        </NXGDSButton>
      </div>
    </template>
  </NXGDSModal>
</template>

<style lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.st-voucher-details-modal {
  &__header {
    display: flex;
    justify-content: space-between;
    gap: var(--nxgds-alias-spacing-xs);
    flex-direction: column;

    &-title {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      @include typography('h4', 'regular');
    }

    &-subtitle {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      color: var(--nxgds-mapped-color-text-secondary);
      @include typography('label', 'regular');
    }
  }

  &__table {
    width: 100%;
    padding: var(--nxgds-alias-spacing-2xs);
    background: var(--nxgds-mapped-color-surface-component-subsection);
    border-collapse: separate;
    border-spacing: 0 var(--nxgds-alias-spacing-3xs);
    border-radius: var(--nxgds-alias-radius-corner-md);
    color: var(--nxgds-mapped-color-text-secondary);

    &-tbody-row {
      color: var(--nxgds-mapped-color-text-primary);
      border-radius: var(--nxgds-alias-radius-corner-md);
      background-color: var(--nxgds-mapped-color-surface-component-card-default);
    }

    &-cell {
      padding: 0 var(--nxgds-alias-spacing-sm);

      &:first-child {
        padding-left: var(--nxgds-alias-spacing-md);
        border-top-left-radius: var(--nxgds-alias-radius-corner-md);
        border-bottom-left-radius: var(--nxgds-alias-radius-corner-md);
      }

      &:last-child {
        padding-right: var(--nxgds-alias-spacing-md);
        border-top-right-radius: var(--nxgds-alias-radius-corner-md);
        border-bottom-right-radius: var(--nxgds-alias-radius-corner-md);
      }
    }

    &-thead-row &-cell {
      height: 40px;
      @include typography('label', 'medium');
    }

    &-tbody-row &-cell {
      height: 52px;
      @include typography('label', 'regular');
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
  }
}
</style>
